import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionHeading,
  SectionWrap,
  FadeUp,
  CtaBand,
} from "@/components/sections/Primitives";

export default function OurStoryPage() {
  useSeo({
    title: "Our Story | Pizza Excellente",
    description:
      "Learn the story behind Pizza Excellente, a neighborhood-first pizza brand inspired by authentic Boston-style pizza heritage and built for India.",
    path: "/our-story",
    ogImage: ASSETS.pizza_prep,
    ogImageAlt: "The Pizza Excellente story",
  });

  return (
    <div data-testid="our-story-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-48 pb-16 md:pb-24 bg-pe-mauve/40 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionEyebrow>Our Story</SectionEyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-ink mt-6 leading-[1.02]">
              A pizza brand built on quality, warmth, and{" "}
              <span className="italic text-pe-maroon">neighborhood relevance.</span>
            </h1>
            <p className="mt-8 text-pe-ink/70 text-lg leading-relaxed max-w-xl">
              Pizza Excellente was created to bring authentic pizza, dependable service, and a better everyday food experience to India’s residential communities.
            </p>
            <a
              data-testid="our-story-hero-cta"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-10 bg-pe-maroon text-white hover:bg-pe-maroon-deep transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              Contact Us <ArrowRight size={16} />
            </a>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={ASSETS.pizza_prep}
                alt="Pizza Excellente — homemade Italian-style pizza heritage"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 — Where it began */}
      <SectionWrap testId="story-section-began" tone="cream">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-4 md:sticky md:top-32">
            <SectionHeading className="mt-5">Where it began.</SectionHeading>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-8 space-y-6 text-pe-ink/75 text-lg leading-relaxed">
            <p>
              Pizza Excellente is a US-inspired brand influenced by a renowned downtown Boston pizzeria that has been
              serving authentic, homemade Italian-style pizza for over 50 years. Its long-standing reputation for taste,
              quality, and loyalty helped shape the foundation of the Pizza Excellente concept.
            </p>
            <p>
              Rather than simply replicate an American pizza store, Pizza Excellente reinterprets that inspiration for
              India through a modern quick-service format that suits local neighborhoods, evolving tastes, and delivery-led convenience.
            </p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 2 — Why we exist */}
      <SectionWrap testId="story-section-why-exist" tone="white">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <FadeUp className="md:col-span-6 order-2 md:order-1">
            <SectionHeading className="mt-5">Why Pizza Excellente exists.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              The concept was created to serve residential communities rather than malls, high streets, or transient traffic zones.
              We saw a gap for a pizza brand that feels both premium and accessible — better quality than mass-market chains, yet rooted in convenience and neighborhood relevance.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed">
              Pizza Excellente is designed to deliver fast takeout, welcoming dine-in, premium ingredients, and a customer experience built around consistency and convenience.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-6 order-1 md:order-2">
            <div className="aspect-[5/4] overflow-hidden">
              <img src={ASSETS.cafe_interior} alt="Upscale Pizza Excellente neighborhood cafe interior" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 3 + 4 — Mission & vision */}
      <SectionWrap testId="story-mission-vision" tone="ink">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24">
          <FadeUp>
            <SectionEyebrow>
              <span className="text-pe-orange">Mission</span>
            </SectionEyebrow>
            <p className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl text-pe-cream leading-[1.15]">
              Serving families and communities with the warmth of fresh, high-quality and gluten-free pizza —
              <span className="italic text-pe-rose-soft"> fast, friendly, and close to home.</span>
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SectionEyebrow>
              <span className="text-pe-orange">Vision</span>
            </SectionEyebrow>
            <p className="mt-8 font-serif text-3xl md:text-4xl lg:text-5xl text-pe-cream leading-[1.15]">
              To be the neighborhood’s most preferred pizza brand, setting standards for{" "}
              <span className="italic text-pe-rose-soft">convenience, quality, and value</span> in every residential community.
            </p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 5 — Leadership */}
      <SectionWrap testId="story-leadership" tone="cream">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>Leadership</SectionEyebrow>
            <SectionHeading className="mt-5">Founder-led, consultant-supported.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              Pizza Excellente is led by Aaron, an engineer and MBA with experience across R&D, manufacturing,
              software development, product management, and prior franchisor experience in the United States.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7 space-y-5">
            {[
              {
                name: "Aaron",
                role: "Founder",
                body: "Engineer and MBA with leadership roles at Fortune 500s and dynamic SMEs. Prior franchisor experience in the United States, complemented by hands-on operational training at a renowned 50-year-old Boston pizzeria.",
              },
              {
                name: "Pranav",
                role: "Operations",
                body: "Former Pizza Hut manager with 10+ years in F&B. Will oversee the establishment of the first outlet, lead staff training and operations, followed by managing franchise operations.",
              },
              {
                name: "Cover & Pax",
                role: "Kitchen Consultant",
                body: "A full-service F&B consulting firm based in South Delhi with extensive experience working with pizza outlets — recipes, food cost, menu engineering, SOPs, and pre-opening staff training.",
              },
              {
                name: "Nail and Hammer",
                role: "Restaurant Designer",
                body: "Commercial interior restaurant designer based in New Delhi, leading the store layout and kitchen workflow for the QSR format.",
              },
            ].map((p) => (
              <div key={p.name} className="border-l-2 border-pe-maroon pl-6 py-2">
                <div className="eyebrow text-pe-rose">{p.role}</div>
                <h3 className="font-serif text-2xl md:text-3xl text-pe-ink mt-1">{p.name}</h3>
                <p className="mt-3 text-pe-ink/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 6 — Operations strength */}
      <SectionWrap testId="story-operations" tone="mauve">
        <FadeUp className="max-w-3xl">
          <SectionEyebrow>Operations strength</SectionEyebrow>
          <SectionHeading className="mt-5">Strategic leadership, hands-on expertise.</SectionHeading>
          <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
            The team includes experienced restaurant professionals — a former Pizza Hut manager, culinary experts with strong
            pizza operations backgrounds, and a head chef from our kitchen consulting partner with 5-Star Hotel experience.
            This combination supports quality, efficiency, and consistency across the system.
          </p>
        </FadeUp>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "50+", l: "Years of inspired heritage" },
            { v: "10+", l: "Yrs F&B operations lead" },
            { v: "2 wks", l: "On-site pre-opening" },
            { v: "30", l: "Initial menu items" },
          ].map((s) => (
            <div key={s.l} className="border-t border-pe-ink/20 pt-5">
              <div className="font-serif text-4xl md:text-5xl text-pe-maroon leading-none">{s.v}</div>
              <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">{s.l}</div>
            </div>
          ))}
        </div>
      </SectionWrap>

      <CtaBand
        testId="story-cta-band"
        eyebrow="Next step"
        heading="Explore the franchise opportunity."
        body="See how Pizza Excellente's neighborhood-first model, store format, and franchise support translate into long-term partnership."
        ctaLabel="Explore the Opportunity"
        ctaHref="/franchise-opportunity"
        secondaryLabel="Contact Us"
        secondaryHref={BRAND.mailto}
      />
    </div>
  );
}
